import type { LngLatLike } from 'mapbox-gl';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { LayerStoreService, LEVEL_LAYER } from './layer-store.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { SelectedCellService } from './selected-cell.service';
import { MapBoxService } from '../mapbox.service';
import { PopupService } from './popup.service';
import { BreakpointObserverService } from '../../../shared/services/breakpoint-observer.service';
import { allFeatureNames } from '../../../shared/types/feature-data-type';
import {
  LAST_VISIT_KEY,
  ONE_DAY_MS,
} from '../../../shared/services/last-visit.service';
import { pickPageMeta } from '../../../seo/seo-config.data';

export interface MapUrlParams {
  coordinates?: LngLatLike | null;
  zoom?: number | null;
  level?: LEVEL_LAYER | null;
  selectedCellId?: string | null;
  currentLayer?: string | null;
}

export const MAP_QUERY_PARAMS = 'MAP_QUERY_PARAMS';

@Injectable({
  providedIn: 'root',
})
export class MapUrlService {
  private readonly coordinatesParam = 'll';
  private readonly coordinatesFractionDigits = 5;
  private readonly coordinatesSeparator = ',';
  private readonly zoomParam = 'z';
  private readonly levelParam = 'lv';
  private readonly idParam = 'id';
  private readonly layerParam = 'lr';

  constructor(
    private location: Location,
    private layerStore: LayerStoreService,
    private selectedCellService: SelectedCellService,
    private mapboxService: MapBoxService,
    private popupService: PopupService,
    private toastr: ToastrService,
    private breakpointObserverService: BreakpointObserverService
  ) {}

  /**
   * @return current url get params as {@link MapUrlParams} object.
   **/
  public getUrlParams(query?: string): MapUrlParams {
    let searchParams: URLSearchParams;

    if (query) {
      searchParams = new URLSearchParams(
        query.startsWith('?') ? query.substring(1) : query
      );
    } else {
      const url = new URL(window.location.href);

      if (!url.search) {
        // Use local storage if no URL search params
        const storedParams = localStorage.getItem(MAP_QUERY_PARAMS);
        if (storedParams) {
          searchParams = new URLSearchParams(storedParams);
        } else {
          searchParams = new URLSearchParams();
        }
      } else {
        // Use URL search params
        searchParams = url.searchParams;
      }
    }

    const coordinates = searchParams.get(this.coordinatesParam);
    const ll = coordinates
      ? coordinates.split(this.coordinatesSeparator).map(parseFloat)
      : null;
    const zoom = searchParams.get(this.zoomParam)
      ? +searchParams.get(this.zoomParam)!
      : null;
    const level: LEVEL_LAYER =
      (searchParams.get(this.levelParam) as LEVEL_LAYER) ?? null;
    const selectedCellId = searchParams.get(this.idParam) ?? null;
    const currentLayer = searchParams.get(this.layerParam) ?? null;

    return {
      coordinates: ll && ll.length === 2 ? [+ll[0], +ll[1]] : null,
      zoom,
      level,
      selectedCellId,
      currentLayer,
    };
  }

  /** Parse pathname like "/boston/crime-rate" or "/crime-rate". Returns pickPageMeta() or null. */
  private getPathMeta(): ReturnType<typeof pickPageMeta> {
    const path = window.location.pathname.replace(/\/+$/, ''); // trim trailing slash
    const parts = path.split('/').filter(Boolean); // ["boston","crime-rate"] or ["crime-rate"]

    if (parts.length === 2) {
      const [cityPath, featurePath] = parts;
      return pickPageMeta({ cityPath, featurePath });
    }
    if (parts.length === 1) {
      const [featurePath] = parts;
      return pickPageMeta({ featurePath });
    }
    return null;
  }

  private paramsFromPathMeta(
    meta: NonNullable<ReturnType<typeof pickPageMeta>>
  ): MapUrlParams {
    const p: MapUrlParams = {};
    // layer (always)
    p.currentLayer = meta.feature.featureConst;
    // level
    if (meta.level) {
      p.level = meta.level;
    }
    // center/zoom only if city exists
    if (meta.city) {
      // meta.city.center = [lng, lat] → MapUrlParams.coordinates expects [lat, lng]
      const [lng, lat] = meta.city.center;
      p.coordinates = [lat, lng];
      p.zoom = meta.city.zoom ?? null;
    }
    return p;
  }

  /**
   * Set GET params in the browser URL without refreshing the page.
   */
  private setUrlParams(params: MapUrlParams) {
    const pathName = window.location.pathname;
    const pathNameWithoutParams = pathName.split('?')[0];
    this.location.go(pathNameWithoutParams + '?' + this.paramsToString(params));
    localStorage.setItem(MAP_QUERY_PARAMS, this.paramsToString(params));
  }

  /**
   *  Change some GET params in the browser URL not erasing others.
   *  Does _not_ refresh the page.
   */
  public updateUrlParams(params: MapUrlParams) {
    const currentParams = this.getUrlParams();
    currentParams.coordinates =
      params.coordinates !== undefined
        ? params.coordinates
        : currentParams.coordinates;
    currentParams.zoom =
      params.zoom !== undefined ? params.zoom : currentParams.zoom;
    currentParams.level =
      params.level !== undefined ? params.level : currentParams.level;
    currentParams.selectedCellId =
      params.selectedCellId !== undefined
        ? params.selectedCellId
        : currentParams.selectedCellId;
    currentParams.currentLayer =
      params.currentLayer !== undefined
        ? params.currentLayer
        : currentParams.currentLayer;
    this.setUrlParams(currentParams);
  }

  /**
   * Format {@link MapUrlParams} as GET params string.
   * Does _not_ add '?' in the beginning.
   */
  private paramsToString(params: MapUrlParams): string {
    let coordinatesStr = '';

    if (params.coordinates != null) {
      const lat = (params.coordinates as [number, number])[0].toFixed(
        this.coordinatesFractionDigits
      );
      const lon = (params.coordinates as [number, number])[1].toFixed(
        this.coordinatesFractionDigits
      );
      coordinatesStr =
        this.coordinatesParam + '=' + lat + this.coordinatesSeparator + lon;
    }

    const zoomStr = this.formatParam(this.zoomParam, params.zoom!);
    const levelStr = this.formatParam(this.levelParam, params.level as string);
    const selectedCellIdStr = this.formatParam(
      this.idParam,
      params.selectedCellId as string
    );
    const currentLayerStr = this.formatParam(
      this.layerParam,
      params.currentLayer as string
    );

    return this.ampersandSeparated([
      coordinatesStr,
      zoomStr,
      levelStr,
      selectedCellIdStr,
      currentLayerStr,
    ]);
  }

  private ampersandSeparated(params: string[]): string {
    // Return empty string if nothing to separate
    if (params.every((el) => el === '')) {
      return '';
    }

    const res: string = params
      .filter((p) => p !== '')
      .map((p) => p + '&')
      .reduce((s1, s2) => s1 + s2);
    return res.replace(/&+$/, ''); // delete last `&`
  }

  private formatParam(key: string, value: string | number | null) {
    return value != null ? key + '=' + value : '';
  }

  public initWithParams(map: mapboxgl.Map, params?: MapUrlParams): void {
    const pathMeta = this.getPathMeta(); // 1) try path
    if (pathMeta) {
      const override = this.paramsFromPathMeta(pathMeta);
      // merge explicit params (if provided) with path override; path wins
      const merged: MapUrlParams = { ...(params ?? {}), ...override };
      // treat as non-default jump
      this.jumpToLocation(map, false, merged);

      this.mapboxService.map.once('idle', () => {
        this.layerStore.onZoomSwitchLayer(
          merged.zoom ?? this.mapboxService.map.getZoom() + 0.01,
          this.breakpointObserverService.isMobile.value
        );

        // Reset location without navigation so that update wont reset user input
        this.location.replaceState('/');
      });
      // persist visit and subscriptions as before
      localStorage.setItem(LAST_VISIT_KEY, new Date(Date.now()).toISOString());
      this.handleSubscriptions();
      return;
    }

    // fallback to query/localStorage
    const now = Date.now();
    const lastVisitStr = localStorage.getItem(LAST_VISIT_KEY);
    const lastVisit = lastVisitStr ? new Date(lastVisitStr).getTime() : 0;
    const isDefaultLocation = now - lastVisit > ONE_DAY_MS;

    // if previous visit ended more than 3 days ago, use default location
    this.jumpToLocation(map, isDefaultLocation, params);

    // record visit for next time
    localStorage.setItem(LAST_VISIT_KEY, new Date(now).toISOString());

    // subscribe anyway
    this.handleSubscriptions();
  }

  private handleSubscriptions(): void {
    this.selectedCellService.selectedCellGeoId.subscribe((selectedCellId) =>
      this.updateUrlParams({ selectedCellId })
    );

    this.layerStore.activeLevel.subscribe((level) =>
      this.updateUrlParams({ level })
    );
    this.layerStore.activeLayer.subscribe((currentLayer) =>
      this.updateUrlParams({ currentLayer })
    );
  }

  public jumpToLocation(
    map: mapboxgl.Map,
    isDefaultLocation: boolean,
    params?: MapUrlParams
  ): void {
    const urlParams = params || this.getUrlParams();

    const center: LngLatLike =
      urlParams.coordinates != null
        ? [
            (urlParams.coordinates as [number, number])[1],
            (urlParams.coordinates as [number, number])[0],
          ]
        : new (window as any).mapboxgl.LngLat(
            this.layerStore.defaultLocation.center[0],
            this.layerStore.defaultLocation.center[1]
          );

    const zoom: number =
      urlParams.zoom != null
        ? urlParams.zoom
        : this.layerStore.defaultLocation.zoom;

    //handle cases when map is loaded and being zoomed in one state which doesn't allow it to color properly
    this.layerStore.onZoomSwitchLayer(
      zoom + 0.01,
      this.breakpointObserverService.isMobile.value
    );

    // do not jumpTo if location is set to default
    if (!isDefaultLocation) {
      map.jumpTo({
        center: center,
        zoom: zoom,
      });
    }

    // Handle main map specific params like level and cell id separately as other maps do not require same params
    this.handleMainMapInit(urlParams, zoom, isDefaultLocation);
  }

  private handleMainMapInit(
    urlParams: MapUrlParams,
    zoom: number,
    isDefaultLocation: boolean
  ): void {
    const map = this.mapboxService.map;
    const level = urlParams.level != null ? urlParams.level : undefined;
    const selectedCellId = urlParams.selectedCellId
      ? urlParams.selectedCellId
      : undefined;
    const layer =
      urlParams.currentLayer != null ? urlParams.currentLayer : undefined;

    if (level && !isDefaultLocation) {
      this.layerStore.handleLevelChange(level);
    } else {
      this.layerStore.onZoomSwitchLayer(
        zoom,
        this.breakpointObserverService.isMobile.value
      );
    }

    if (layer && !isDefaultLocation) {
      if (allFeatureNames.includes(layer)) {
        this.layerStore.handleLayerChange(layer);
      } else if (allFeatureNames.includes(layer.toUpperCase())) {
        this.layerStore.handleLayerChange(layer.toUpperCase());
      } else if (allFeatureNames.includes(layer.toLowerCase())) {
        this.layerStore.handleLayerChange(layer.toLowerCase());
      } else {
        this.layerStore.handleLayerChange(this.layerStore.DEFAULT_LAYER);
      }
    } else {
      this.layerStore.handleLayerChange(this.layerStore.DEFAULT_LAYER);
    }

    if (selectedCellId && map) {
      map.once('idle', () => {
        const feature = map
          .queryRenderedFeatures(undefined, {
            layers: [this.layerStore.activeLevel.value],
          })
          .find((feature) => {
            return feature.properties!.external_id === selectedCellId;
          });

        if (feature) {
          this.mapboxService.simulateMapClick(
            this.mapboxService.getFeatureCenter(feature)
          );
        }
      });
    }
  }

  public getUrl(): string {
    return this.location.path().split('?')[0];
  }

  public copyUrlToClipboard(): void {
    const query = this.location.path();
    const url = `${environment.domainUrl}${query}`;

    navigator.clipboard
      .writeText(url)
      .then(() => this.toastr.success('Location is copied to clipboard'));
  }
}
